// Entry point: boots the renderer, sound, input and lobby UI, then runs one requestAnimationFrame loop for
// whichever host is live: a local SoloSession (solo), an OnlineSession (online / play with friends), or a
// bots-only world behind the lobby, so the arena is always moving like the original. Both game hosts hand the
// renderer the same Scene, and their sim events drive the toasts/confetti (renderer.fx) and the sounds.
// The world itself is drawn in 3D (render3d, WebGPU or WebGL2), loaded lazily under the 2D overlay; without
// either, the 2D renderer draws everything.
import { BOOST_MIN_MASS, EXCITE_AT, POWERUP_TYPES, type DifficultyId } from '../shared/config';
import type { PlayerInput, Scene, SimEvent } from '../shared/types';
import { Ads } from './ads';
import { api, ApiError, type Profile } from './api';
import { Sound } from './audio';
import { Input, type InputState } from './input';
import { readHooks, seededRng } from './hooks';
import { Installer } from './install';
import { OnlineSession } from './net';
import { Renderer, massCentre } from './render/renderer';
import { initialTier } from './render3d/quality';
import { SoloSession } from './solo';
import { UI, deathLine, deathStats, type PlayMode, type PlayOptions } from './ui';

type DeathEvent = Extract<SimEvent, { t: 'death' }>;
interface SoloGame { kind: 'solo'; s: SoloSession; dead: boolean }
interface OnlineGame {
  kind: 'online'; s: OnlineSession; room: string;
  live: boolean; // WELCOME + first SNAPSHOT arrived
  startedAt: number; // ms, for the join timeout
  dead: boolean; // death screen is up (or about to be)
  wasAlive: boolean; downAt: number; // fallback when a death event never arrives
}
// matchmaking (GET /api/match) before there is a socket: Esc / Cancel must still back out
interface JoiningGame { kind: 'joining' }
type Game = { kind: 'lobby' } | JoiningGame | SoloGame | OnlineGame;

const MAX_DT = 0.05; // same clamp as the sim
const AIM_REACH = 400; // idle touch joystick: split/feed aim this far along the last direction (prototype aimed())
const JOIN_TIMEOUT_MS = 12000;
const DEATH_FALLBACK_MS = 1500; // dead on screen with no death event this long: show the death screen anyway
const LEAVE_REFRESH_MS = 1500; // the Arena writes a leaver's points in the background
const ERROR_LOG_GAP_MS = 5000;
const CLOSE_TEXT: Record<string, string> = {
  'room full': 'That room is full. Try again in a moment.',
  replaced: 'You joined this room from another tab.',
  flood: 'Disconnected: too many messages.',
  'bad message': 'Disconnected by the server.',
  'connection failed': 'Couldn\'t connect to the game server. Try again.',
  disconnected: 'Lost the connection to the server.',
  timeout: 'The room didn\'t answer. Try again.',
  idle: 'You were idle for a while, so you left the room.',
  silent: 'Lost the connection to the server.',
  'joined elsewhere': 'You joined another room from another tab.',
  stalled: 'The game stopped updating. Try again.',
  expired: 'Your blob was lost while reconnecting. Its points were saved.',
};

// ========== boot ==========
const hooks = readHooks(location.search);
const canvas = document.getElementById('c') as HTMLCanvasElement;
let worldCanvas = document.getElementById('world') as HTMLCanvasElement | null;
const renderer = new Renderer(canvas);
// no world behind the lobby until the 3D layer has loaded (a game started before then draws in 2D)
renderer.setMode(hooks.gl === '2d' || !worldCanvas ? 'full' : 'pending');
const sound = new Sound();
const input = new Input(canvas);
// what screenshot and benchmark scripts read: the drawing path, the quality tier, frames per second
interface Debug {
  mode: string; backend: string; tier: string; fps: number; ready: boolean;
  game: string; alive: boolean; room: string; humans: number; board: string[]; // what the end-to-end test checks
  me: { x: number; y: number }; // my mass centre (world units)
  bench(frames: number): Promise<number>; // ms per frame, GPU work included (the world drawn again and again)
}
const debug: Debug = { mode: renderer.mode, backend: 'none', tier: 'none', fps: 0, ready: false, game: 'lobby', alive: false, room: '', humans: 0, board: [], me: { x: 0, y: 0 },
  bench: frames => bench2d(frames) };
// the 2D renderer's cost (fallback, and the baseline before 3D): the lobby world redrawn, synced by a pixel read
async function bench2d(frames: number): Promise<number> {
  const scene = backdrop.scene(), ctx = canvas.getContext('2d');
  renderer.render(scene, 0, { running: false, touch: false }); ctx?.getImageData(0, 0, 1, 1);
  const t0 = performance.now();
  for (let i = 0; i < frames; i++) renderer.render(scene, 1 / 60, { running: true, touch: false });
  ctx?.getImageData(0, 0, 1, 1);
  return (performance.now() - t0) / frames;
}
(window as unknown as { __cg: Debug }).__cg = debug;
// ?clip=1 (scripts/clips): a capture script may steer my blob with pilot(scene) -> PlayerInput (world target),
// given the last frame's scene (solo and online), and fast-forward: `skip` frames are stepped (solo) or played
// back (online) without drawing. Only exists with the hook.
interface ClipHook { pilot: ((scene: Scene) => PlayerInput | null) | null; skip: number }
const clipHook: ClipHook | null = hooks.clip ? { pilot: null, skip: 0 } : null;
if (clipHook) (window as unknown as { __cgClip: ClipHook }).__cgClip = clipHook;
if (hooks.shot) document.body.classList.add('shot');
let profile: Profile | null = null;
let game: Game = { kind: 'lobby' };
let joinSeq = 0; // bumps on every start/leave, so a late api.match() reply can't start a stale game
let unlockHeard = false; // the 'unlock' sparkle plays once per visit, like the toast
let lastErrorAt = 0;
const centre = { x: 0, y: 0 };
let lastSolo: Scene | null = null; // last solo frame's scene (for idle-joystick aiming)

// the ad tier: unpaid profiles under FREE_PLAY. An ad mutes the game's sound and gives it back afterwards.
let adMuted = false;
const ads = new Ads({
  test: new URLSearchParams(location.search).get('adtest') === '1',
  mute: on => {
    if (on && !sound.muted) { sound.setMuted(true); adMuted = true; }
    else if (!on && adMuted) { sound.setMuted(false); adMuted = false; }
  },
});
function adsFor(p: Profile): void {
  if (p.unlocked) ads.disable();
  else if (p.free !== undefined) { ads.configure(p.ads ?? null); ads.banner(); }
}

const ui = new UI({
  onPlay: (mode, opts) => ads.preroll(() => play(mode, opts)),
  onAgain: () => ads.next(() => again()),
  onLobby: () => toLobby(),
  onCancel: () => toLobby(),
  onProfile: p => { profile = p; adsFor(p); },
  onUnlock: () => { ads.disable(); renderer.fx.burst(140); sound.success(); },
  onPurchase: () => sound.success(),
  onView: v => setView(v),
}, sound);
input.onEscape = () => toLobby(); // Esc, and the touch Exit button

// The tab is hidden or the page is going away. A hidden tab gets no animation frames, so nothing below
// runs again until it's back: let go of everything now. A live solo life posts what it has with
// sendBeacon (mobile browsers may kill a hidden tab without another event); online, the Arena is told
// I'm holding nothing (it also goes neutral by itself after 1 s without INPUT).
function pageAway(): void {
  input.releaseAll();
  sound.setBoosting(false);
  const g = game;
  if (g.kind === 'solo' && !g.dead) g.s.beacon();
  else if (g.kind === 'online') g.s.sendNeutral();
}
document.addEventListener('visibilitychange', () => { if (document.hidden) pageAway(); });
window.addEventListener('pagehide', () => pageAway());

// browsers only allow audio after a gesture: every gesture creates/resumes the context (cheap when running)
for (const type of ['pointerdown', 'keydown', 'touchend'] as const) {
  window.addEventListener(type, () => sound.unlock(), { capture: true, passive: true });
}

// the world behind the lobby: bots only, no power-ups (nothing gets excited without a human alive)
function backdropWorld(difficulty: DifficultyId): SoloSession {
  const s = new SoloSession({ name: 'Player', skin: 'blue', difficulty, excite: () => hooks.fx, report: false,
    rng: hooks.seed !== null ? seededRng(hooks.seed) : undefined });
  s.world.removePlayer(s.myId);
  s.drainEvents();
  if (hooks.fx) showOffEffects(s);
  return s;
}
// ?fx=1: a still tableau of every effect at the arena centre, for screenshots: shield + magnet on one bot,
// ghost + speed on another, the Golden Blob, and one power-up of each kind. The lobby world stops moving
// (shader time still animates rings, bobbing and wobble).
function showOffEffects(s: SoloSession): void {
  const w = s.world, now = w.now, mass = (p: { cells: { m: number }[] }) => p.cells.reduce((a, c) => a + c.m, 0);
  w.startEvent('gold');
  const place = (p: (typeof w.players)[number] | undefined, x: number, y: number) => {
    p?.cells.forEach((c, i) => { c.x = x + (i % 3) * c.r * 1.6; c.y = y + Math.floor(i / 3) * c.r * 1.6; });
  };
  const bots = w.players.filter(p => p.alive && !p.golden).sort((a, b) => mass(b) - mass(a));
  if (bots[0]) bots[0].fx = { shield: now + 1e6, magnet: now + 1e6 };
  if (bots[1]) bots[1].fx = { ghost: now + 1e6, speed: now + 1e6 };
  place(bots[0], 2000, 2000); place(bots[1], 2380, 2160); place(w.players.find(p => p.golden), 1620, 1880);
  POWERUP_TYPES.forEach((type, i) => w.powerups.push({ id: 9000 + i, type, x: 1780 + i * 150, y: 1720, ph: i * 1.3, born: now - 1 }));
  s.drainEvents();
  renderer.camera.x = 2000; renderer.camera.y = 1950; renderer.camera.z = 0.9;
}
let backdrop = backdropWorld(ui.difficulty);
if (hooks.cam) { renderer.camera.x = hooks.cam.x; renderer.camera.y = hooks.cam.y; renderer.camera.z = hooks.cam.z; }

// ========== starting and leaving games ==========
function play(mode: PlayMode, opts: PlayOptions): void {
  leaveGame();
  if (renderer.mode === 'pending') renderer.setMode('full'); // no 3D yet: this game is drawn in 2D throughout
  if (mode === 'solo') startSolo(opts.difficulty);
  else void startOnline(mode, opts.room);
}

// a fresh life on screen: lobby/death panels away, controls live, no leftover toasts or confetti
function begin(): void {
  ui.hideLobby();
  ui.setConnecting(false);
  input.setActive(true);
  renderer.fx.reset();
  (document.activeElement as HTMLElement | null)?.blur?.(); // keys typed into the name box must reach the game
}

function startSolo(difficulty: DifficultyId): void {
  const s = new SoloSession({
    name: ui.name, skin: ui.skin, difficulty,
    rng: clipHook && hooks.seed !== null ? seededRng(hooks.seed) : undefined, // ?clip=1: the same match every time
    report: !!profile?.unlocked || !!profile?.free, // locked solo without FREE_PLAY plays for fun: no ticket, no points
    // power-ups and events: 700+ saved points (SoloSession adds "or 700+ mass this match")
    excite: () => (profile?.points ?? 0) >= EXCITE_AT,
    onReport: (p, _result, awarded) => {
      ui.setProfile(p);
      if (game.kind === 'solo' && game.s === s && game.dead) ui.setDeathPoints(awarded);
    },
  });
  game = { kind: 'solo', s, dead: false };
  lastSolo = null;
  begin();
}

async function startOnline(mode: Exclude<PlayMode, 'solo'>, room0: string | undefined): Promise<void> {
  const seq = ++joinSeq;
  game = { kind: 'joining' };
  ui.hideLobby();
  ui.setConnecting(true, mode === 'friends' ? 'Joining your private room…' : 'Finding a room…');
  input.setActive(true); // Esc (and the overlay's Cancel) back out while matchmaking
  (document.activeElement as HTMLElement | null)?.blur?.();
  let room = room0;
  if (!room) {
    try {
      room = (await api.match()).room;
    } catch (err) {
      if (seq !== joinSeq) return;
      const st = err instanceof ApiError ? err.status : 0;
      backToLobby(st === 503 ? 'All rooms are full right now. Try again in a minute.'
        : st === 401 || st === 403 ? (profile?.free ? 'Couldn\'t start online play. Reload and try again.' : 'Unlock the game to play online.')
        : 'Couldn\'t reach the server. Try again.');
      return;
    }
    if (seq !== joinSeq) return; // gave up (Esc / lobby) while matchmaking
  }
  ui.setConnecting(true, 'Connecting…');
  const s: OnlineSession = new OnlineSession(room, {
    onClose: reason => {
      if (game.kind !== 'online' || game.s !== s) return;
      backToLobby(CLOSE_TEXT[reason] ?? `Disconnected: ${reason}`);
    },
    // the socket dropped and is being replaced: the last picture stays up under a note
    onReconnecting: on => {
      if (game.kind !== 'online' || game.s !== s) return;
      ui.setConnecting(on, 'Reconnecting…');
    },
  });
  s.setView(innerWidth, innerHeight);
  game = { kind: 'online', s, room, live: false, startedAt: performance.now(), dead: false, wasAlive: false, downAt: 0 };
  input.setActive(true); // Esc works while connecting
  (document.activeElement as HTMLElement | null)?.blur?.();
  s.connect();
}

// "Play again" on the death screen: a new life in the same world / room
function again(): void {
  const g = game;
  if (g.kind === 'solo') { g.s.respawn(); g.dead = false; begin(); return; }
  if (g.kind === 'online') { g.s.respawn(); g.dead = false; g.wasAlive = false; g.downAt = 0; begin(); return; }
  ui.showLobby();
}

// Esc, the touch Exit button, "Back to lobby", Cancel while connecting
function toLobby(): void {
  if (game.kind === 'lobby') return;
  leaveGame();
  ui.showLobby();
}

// Ends whatever is running (bumping joinSeq drops a matchmaking reply still on its way). A solo life cut
// short still posts its points; its world stays on as the lobby backdrop (as in the original). Leaving an
// online room alive is paid out by the Arena.
function leaveGame(): void {
  const g = game;
  joinSeq++;
  game = { kind: 'lobby' };
  input.setActive(false);
  sound.setBoosting(false);
  if (g.kind === 'solo') {
    void g.s.leave();
    g.s.world.removePlayer(g.s.myId);
    backdrop = g.s;
  } else if (g.kind === 'online') {
    g.s.close();
    if (g.live && !g.dead) setTimeout(() => void ui.refresh(), LEAVE_REFRESH_MS);
  }
}

function backToLobby(message: string): void {
  leaveGame();
  ui.showLobby();
  ui.notify(message);
}

function showDeath(e: DeathEvent, points?: number): void {
  input.setActive(false);
  sound.setBoosting(false);
  ui.showDeath({ title: deathLine(e.killerName), stats: deathStats(e), points, score: e.peakMass });
}

// ========== per frame ==========
// screen offset from the centre -> world offset from the camera point (the camera follows my mass centre);
// over the 3D world this is the floor point under the pointer, which its tilted camera needs
function steer(inp: InputState): { x: number; y: number } {
  return renderer.aimOffset(inp.ax, inp.ay);
}
// idle touch joystick: split and feed fire along the last direction moved, without moving (prototype aimed())
const idleAim = () => ({ x: input.lastDir.x * AIM_REACH, y: input.lastDir.y * AIM_REACH });

// sounds and celebrations for one batch of events, then the picture
let shown = 0;
function present(scene: Scene, events: SimEvent[], dt: number, boostHeld: boolean, running: boolean): void {
  if (++shown % 15 === 0) {
    debug.game = game.kind; debug.alive = scene.alive; debug.room = scene.roomName; debug.humans = scene.humans;
    debug.board = scene.leaderboard.map(r => r.name);
  }
  massCentre(scene.myCells, debug.me);
  if (events.length) {
    renderer.fx.onSimEvents(events, scene, renderer.camera);
    sound.onSimEvents(forSound(events, scene.myId), scene.myId);
  }
  sound.setBoosting(running && boostHeld && scene.alive && scene.myMass > BOOST_MIN_MASS);
  renderer.render(scene, dt, { running, touch: input.touchMode });
}
// the World says 'unlock' on every new solo life; the sparkle, like the toast, is once per visit
function forSound(events: SimEvent[], myId: number): SimEvent[] {
  if (!events.some(e => e.t === 'unlock' && e.player === myId)) return events;
  if (!unlockHeard) { unlockHeard = true; return events; }
  return events.filter(e => e.t !== 'unlock');
}

function soloFrame(g: SoloGame, dt: number): void {
  const inp = input.poll(), s = g.s, cam = renderer.camera;
  // solo targets are world points: the camera centre plus the screen offset
  const a = steer(inp);
  const pi: PlayerInput = { tx: cam.x + a.x, ty: cam.y + a.y, split: inp.split, eject: inp.eject, boost: inp.boost };
  if (input.idle && lastSolo && massCentre(lastSolo.myCells, centre)) {
    const d = idleAim();
    pi.aimX = centre.x + d.x; pi.aimY = centre.y + d.y;
  }
  s.step(dt, clipHook?.pilot?.(lastSolo ?? s.scene()) ?? pi);
  const scene = s.scene(), events = s.drainEvents(); // drainEvents posts my result when I die
  lastSolo = scene;
  if (clipHook && clipHook.skip > 0) { clipHook.skip--; return; }
  present(scene, events, dt, inp.boost, !g.dead && scene.alive);
  for (const e of events) {
    if (e.t === 'death' && e.player === s.myId && !g.dead) { g.dead = true; showDeath(e); }
  }
}

function onlineFrame(g: OnlineGame, t: number, dt: number): void {
  const s = g.s;
  s.setView(innerWidth, innerHeight); // the Arena sizes my interest box from it (sent only when it changes)
  const scene = s.scene(t);
  if (!scene) {
    if (t - g.startedAt > JOIN_TIMEOUT_MS) { backToLobby(CLOSE_TEXT.timeout); return; }
    input.poll(); // drop presses made while connecting
    backdropFrame(dt);
    return;
  }
  if (!g.live) { g.live = true; begin(); }
  if (clipHook && clipHook.skip > 0) { clipHook.skip--; s.drainEvents(); return; } // ?clip=1 fast-forward
  const inp = input.poll();
  // INPUT is relative to my mass centre, which the camera follows
  const live = scene.alive, idle = input.idle, a = !live ? { x: 0, y: 0 } : idle ? idleAim() : steer(inp);
  const pil = live ? clipHook?.pilot?.(scene) : null;
  if (pil && massCentre(scene.myCells, centre)) {
    s.sendInput({ dx: pil.tx - centre.x, dy: pil.ty - centre.y, split: pil.split, eject: pil.eject, boost: pil.boost, aim: false });
  } else s.sendInput({ dx: a.x, dy: a.y, split: inp.split && live, eject: inp.eject && live, boost: inp.boost && live, aim: idle && live });
  const events = s.drainEvents();
  present(scene, events, dt, inp.boost, !g.dead && scene.alive);
  for (const e of events) {
    if (e.t === 'death' && e.player === scene.myId && !g.dead) { g.dead = true; showDeath(e, e.points); }
  }
  // a lost death event must not leave me staring at an empty arena
  if (scene.alive) { g.wasAlive = true; g.downAt = 0; }
  else if (g.wasAlive && !g.dead) {
    if (!g.downAt) g.downAt = t;
    else if (t - g.downAt > DEATH_FALLBACK_MS) {
      g.dead = true;
      input.setActive(false);
      sound.setBoosting(false);
      ui.showDeath({ title: deathLine(''), stats: [], delayMs: 0 });
    }
  }
}

function backdropFrame(dt: number): void {
  const s = backdrop;
  s.step(hooks.fx ? 0 : dt, { tx: NaN, ty: NaN, split: false, eject: false, boost: false });
  s.drainEvents(); // bots only: nothing to celebrate
  const scene = s.scene();
  if (game.kind === 'lobby' && !hooks.cam && !hooks.fx) attract(scene, dt);
  renderer.render(scene, dt, { running: false, touch: input.touchMode });
}
// behind the lobby the camera drifts after the biggest blob, so the arena always shows some play
const ATTRACT_RATE = 0.5, ATTRACT_ZOOM = 0.8;
function attract(scene: Scene, dt: number): void {
  let big: Scene['cells'][number] | null = null;
  for (const c of scene.cells) if (!big || c.mass > big.mass) big = c;
  if (!big) return;
  const cam = renderer.camera, k = 1 - Math.exp(-ATTRACT_RATE * dt);
  const z = Math.max(0.45, Math.sqrt(innerWidth * innerHeight / 1.6e6)) * ATTRACT_ZOOM;
  cam.x += (big.x - cam.x) * k; cam.y += (big.y - cam.y) * k; cam.z += (z - cam.z) * k;
}

// ========== the 3D world ==========
// WebGPU, else WebGL2 (render3d/world.ts); anything failing on the way leaves the 2D renderer drawing it all
function setBackend(name: string): void {
  debug.backend = name; debug.mode = renderer.mode;
  document.documentElement.dataset.gl = name;
}
// The world is built and attached only while the lobby is up: building it (grass, marble, every pipeline) takes
// a noticeable moment, and swapping the arena under a live game would freeze and blank it. A game started before
// it arrived plays out in 2D. Loading that hangs leaves the lobby on the 2D arena after BOOT_PATIENCE_MS.
const BOOT_PATIENCE_MS = 8000;
const lobbyUp = (): Promise<void> => new Promise(res => {
  const check = () => (game.kind === 'lobby' ? res() : setTimeout(check, 300));
  check();
});
function worldLost(): void {
  world3d = null; booting = null;
  debug.bench = bench2d;
  tierOf = () => 'none';
  setBackend('2d');
}
renderer.onWorldLost = err => { console.warn('3D world failed, drawing in 2D', err); worldLost(); };
// The lobby's Graphics choice: 2D unhooks the world layer (kept, not disposed, so 3D comes straight back) and
// the 2D renderer draws everything; 3D hooks it back, building it the first time. Only switched from the lobby.
let world3d: Awaited<ReturnType<typeof import('./render3d/world').createWorld>> | null = null;
let booting: Promise<void> | null = null, failed3d = false;
function setView(v: '3d' | '2d'): void {
  if (hooks.gl === '2d' || failed3d) return;
  if (v === '2d') {
    if (renderer.mode === 'overlay') renderer.detachWorld();
    else if (renderer.mode === 'pending') renderer.setMode('full');
    world3d?.canvas.classList.remove('on');
    setBackend('2d');
    return;
  }
  if (world3d) {
    renderer.attachWorld(world3d);
    world3d.canvas.classList.add('on');
    setBackend(world3d.backend);
    return;
  }
  if (!booting) { renderer.setMode('pending'); booting = bootWorld(); }
}
async function bootWorld(gl = hooks.gl === 'webgl2' ? 'webgl2' as const : undefined): Promise<void> {
  if (hooks.gl === '2d' || !worldCanvas) { setBackend('2d'); debug.ready = true; return; }
  const patience = setTimeout(() => { if (renderer.mode === 'pending') renderer.setMode('full'); }, BOOT_PATIENCE_MS);
  try {
    const { createWorld } = await import('./render3d/world');
    await lobbyUp();
    const canvasNow = worldCanvas;
    const world = await createWorld(canvasNow, {
      gl,
      tier: initialTier({ mobile: input.touchMode, forced: hooks.tier }),
      fixedTier: hooks.tier !== null,
      onLost: (why, retry) => {
        worldLost();
        if (retry && !gl) {
          // WebGPU failed under way: WebGL2 on a fresh canvas (this one holds a WebGPU context), 2D meanwhile
          console.warn(why, '- trying WebGL2');
          const old = world.canvas, fresh = old.cloneNode(false) as HTMLCanvasElement;
          fresh.classList.remove('on');
          renderer.setMode('full');
          old.replaceWith(fresh);
          worldCanvas = fresh;
          booting = bootWorld(retry);
          return;
        }
        console.warn(why, '- drawing in 2D');
        renderer.setMode('full');
      },
    });
    worldCanvas = world.canvas; // WebGL2 may have needed a fresh one
    await lobbyUp(); // a game may have started while it was being built
    world3d = world;
    if (ui.view === '2d') { setBackend('2d'); debug.ready = true; return; }
    renderer.attachWorld(world);
    debug.bench = frames => world.bench(frames);
    setBackend(world.backend);
    debug.tier = world.tier;
    tierOf = () => world.tier;
    // fade in once a couple of frames are on the canvas
    requestAnimationFrame(() => requestAnimationFrame(() => { world.canvas.classList.add('on'); debug.ready = true; }));
  } catch (err) {
    console.warn('3D world unavailable, drawing in 2D', err);
    failed3d = true; ui.no3d();
    renderer.setMode('full');
    setBackend('2d');
    debug.ready = true;
  } finally {
    clearTimeout(patience);
  }
}
let tierOf = () => 'none';

// ?perf=1: frames per second, drawing path and tier, refreshed twice a second
const perfEl = hooks.perf ? document.body.appendChild(Object.assign(document.createElement('div'), { id: 'perf' })) : null;
let fpsFrames = 0, fpsSince = performance.now();
function countFrame(t: number): void {
  fpsFrames++;
  if (t - fpsSince < 500) return;
  debug.fps = Math.round((fpsFrames * 1000) / (t - fpsSince));
  debug.tier = tierOf(); debug.mode = renderer.mode; debug.game = game.kind;
  fpsFrames = 0; fpsSince = t;
  if (perfEl) perfEl.textContent = `${debug.fps} fps · ${debug.backend} · ${debug.tier} · ${Math.min(2, devicePixelRatio || 1)}x`;
}

let lastT = performance.now();
function frame(t: number): void {
  const dt = Math.min(MAX_DT, Math.max(0, (t - lastT) / 1000));
  lastT = t;
  countFrame(t);
  try {
    const g = game;
    if (g.kind === 'solo') soloFrame(g, dt);
    else if (g.kind === 'online') onlineFrame(g, t, dt);
    else backdropFrame(dt);
  } catch (err) {
    // one bad frame must never stop the loop
    const now = performance.now();
    if (now - lastErrorAt > ERROR_LOG_GAP_MS) { lastErrorAt = now; console.error('frame failed', err); }
  }
  requestAnimationFrame(frame);
}

requestAnimationFrame(frame);
if (ui.view === '3d') booting = bootWorld();
else { renderer.setMode('full'); setBackend('2d'); debug.ready = true; }
new Installer(() => sound.click()); // /install link card + lobby install button
void ui.init();
